import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrainFront, HeartPulse, GraduationCap, Briefcase } from 'lucide-react';
import Seo, { breadcrumbSchema } from '@/components/Seo';
import Breadcrumbs from '@/components/Breadcrumbs';
import SectionHeading from '@/components/SectionHeading';
import Reveal from '@/components/Reveal';
import CTAPanel from '@/components/CTAPanel';
import { CONNECTIVITY, LOCATION_EXTRA, INFRASTRUCTURE, IMAGES } from '@/data/siteData';

const Location = () => (
  <>
    <Seo
      title="Provident Palm Vista Location | Kalyan-Shilphata Road"
      description="Provident Palm Vista location on Kalyan-Shilphata Road — 5 mins from Shilphata Junction, near Diva Station, Thane-Belapur Road, schools, hospitals & business hubs. See connectivity and infrastructure."
      path="/provident-palm-vista-location"
      jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Location', path: '/provident-palm-vista-location' }])]}
    />
    <section className="pt-28 md:pt-36 pb-16 bg-ivory">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Breadcrumbs items={[{ name: 'Location' }]} />
        <Reveal>
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-gold">Location & Connectivity</p>
          <h1 className="font-display text-4xl sm:text-5xl font-medium text-palm mt-3 leading-[1.08] tracking-tight">
            Provident Palm Vista Location – Kalyan-Shilphata Road
          </h1>
          <p className="text-[15px] text-ink/75 mt-5 max-w-3xl leading-relaxed">
            Provident Palm Vista is located at the Junction on Kalyan - Shilphata Road, Daighar Gaon, Mumbai, Maharashtra - 421204. The site sits just 40 metres from Kalyan–Shilphata Road and 800 metres from Panvel Road, placing it at the focal point of the region’s key corridors towards Thane, Panvel and the Navi Mumbai business districts of Airoli, Koparkhairne and Vashi.
          </p>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {CONNECTIVITY.map((g, i) => {
            const icons = [TrainFront, HeartPulse, Briefcase, GraduationCap];
            const Icon = icons[i];
            return (
              <Reveal key={g.cat} delay={i * 0.07}>
                <div className="h-full bg-white border border-line rounded-2xl p-6">
                  <div className="flex items-center gap-2.5 mb-4">
                    <span className="h-9 w-9 rounded-full bg-paper flex items-center justify-center"><Icon className="h-4 w-4 text-gold" /></span>
                    <h2 className="font-semibold text-palm text-sm">{g.cat}</h2>
                  </div>
                  <ul className="space-y-2.5">
                    {g.items.map((it) => (
                      <li key={it.name} className="flex items-center justify-between gap-2 text-[13px]">
                        <span className="text-ink/75">{it.name}</span>
                        <span className="text-gold font-semibold whitespace-nowrap">{it.time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-4">Distances are as per official Provident Housing project material.</p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {LOCATION_EXTRA.map((g) => (
            <Reveal key={g.cat}>
              <div className="h-full bg-white border border-line rounded-2xl p-6">
                <h2 className="font-semibold text-palm text-sm mb-4">{g.cat}</h2>
                <ul className="space-y-2.5">
                  {g.items.map((it) => (
                    <li key={it.name} className="flex items-center justify-between gap-2 text-[13px]">
                      <span className="text-ink/75">{it.name}</span>
                      <span className="text-gold font-semibold whitespace-nowrap">{it.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 md:py-24 bg-paper grain relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <SectionHeading eyebrow="The Neighbourhood" title="Schools, hospitals & workplaces within minutes" />
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <Reveal className="space-y-5 text-[15px] leading-relaxed text-ink/80">
            <p>
              Daily life around Palm Vista is well served. Eurokids Preschool and Guardians School (CBSE) are both about 2.3 km away, with Lodha World School at 4.7 km. For higher education, Government Polytechnic is 3.8 km, Global Jr. College 6.9 km and Swami Vivekanand Jr. College 9.1 km from the site. Healthcare is equally close — GHC Hospitals at 3.7 km, Arnavi Hospital at 5.5 km, Metro Hospital at 6.3 km and Kokilaben Ambani Hospital at 8.8 km.
            </p>
            <p>
              For work, the project connects to Navi Mumbai’s employment districts — Airoli, Koparkhairne and Vashi — with Hexaware Technology at 7.8 km, United Info Tech Park at 7.9 km, and Dhirubhai Ambani Knowledge City and Loma IT Park both about 10 km away.
            </p>
            <p>
              Weekend needs are covered too: Experia Mall (4.9 km), Central Mall (5.4 km), INOX Cinemas (11.9 km) and PVR Cinemas (12 km) are all nearby, while Shree Dutt Mandir (1.5 km) and Ram Mandir (1.7 km) serve daily worship.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <Link to="/provident-palm-vista-amenities" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">Explore amenities <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/2-bhk" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">2 BHK homes <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/gallery" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">View gallery <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/contact" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">Contact us <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <img src={IMAGES.locationMap} alt="Provident Palm Vista location growth map — Shilphata micro-market within the Mumbai Metropolitan Region" className="rounded-3xl w-full shadow-xl border border-line" loading="lazy" width={1080} height={1344} />
          </Reveal>
        </div>
      </div>
    </section>

    <section className="py-16 md:py-24 bg-palm text-ivory">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading dark eyebrow="Infrastructure Pipeline" title="The corridors reshaping Shilphata" sub="The original project material highlighted the following infrastructure projects for the Shilphata node. Where historical expected dates were quoted, verify the current status independently." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {INFRASTRUCTURE.map((inf, i) => (
            <Reveal key={inf.name} delay={i * 0.06}>
              <div className="h-full rounded-2xl border border-ivory/15 bg-ivory/5 p-6 hover:border-gold/40 transition-colors">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-display text-3xl text-gold/50 font-medium">0{i + 1}</p>
                  {inf.status && <span className="rounded-full border border-gold/40 text-gold text-[10px] font-bold tracking-[0.15em] uppercase px-3 py-1">{inf.status}</span>}
                </div>
                <h3 className="font-semibold text-ivory mt-2 text-[15px]">{inf.name}</h3>
                <p className="text-[13px] text-ivory/60 mt-2 leading-relaxed">{inf.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
    <CTAPanel title="Plan Your Visit to the Site" />
  </>
);

export default Location;
